import type { QueryClient } from '@tanstack/react-query'

/**
 * WPS-142 #7: bounded re-poll that surfaces an ASYNCHRONOUS workflow auto-advance
 * on a mounted detail view without a manual reload.
 *
 * A follow-up auto-advance (a Pulse workflow-action binding delivered via Cloud
 * Tasks) commits AFTER the triggering action's HTTP response returns, so a single
 * post-mutation invalidate reads back the pre-advance stage and a mounted detail
 * view (which never polls) keeps showing it. This refetches the detail query a few
 * times over ~14s, stopping as soon as the state key changes or the query is no
 * longer observed (the user navigated away).
 *
 * Shared by the admin and consumer claim hooks so the polling logic lives in one
 * place; each caller supplies its own detail query key, its state-key selector,
 * and what to refresh once the advance lands.
 */
export function scheduleResourceStateRefresh(
  qc: QueryClient,
  opts: {
    /** The detail query to re-poll (e.g. `['claim', uuid]`). */
    detailQueryKey: readonly unknown[]
    /** The stage the resource was in BEFORE the mutation; without a baseline
     *  there is nothing to detect a change against, so the poll is skipped. */
    previousStateKey: string | null | undefined
    /** Read the current state key out of the cached detail payload. */
    selectStateKey: (data: unknown) => string | null
    /** Run once the state key changes (refresh list-derived / dependent queries). */
    onStateAdvanced: () => void
  },
): void {
  const { detailQueryKey, previousStateKey, selectStateKey, onStateAdvanced } =
    opts
  if (previousStateKey == null) return

  const delaysMs = [1200, 2500, 4000, 6000]
  const readStateKey = (): string | null =>
    selectStateKey(qc.getQueryData(detailQueryKey))

  const step = async (index: number): Promise<void> => {
    if (index >= delaysMs.length) return
    await new Promise((resolve) => setTimeout(resolve, delaysMs[index]))
    // Stop if the detail query is no longer observed (user navigated away) so we
    // do not fire background refetches nobody is waiting on.
    const query = qc.getQueryCache().find({ queryKey: detailQueryKey })
    if (!query || !query.isActive()) return
    await qc.refetchQueries({ queryKey: detailQueryKey })
    const current = readStateKey()
    if (current != null && current !== previousStateKey) {
      onStateAdvanced()
      return
    }
    void step(index + 1)
  }
  void step(0)
}
